// frontend/src/features/inventory/pages/LotStatusCatalogsPage.tsx
// AM-056: صفحه مدیریت مستقل کاتالوگ دلایل وضعیت پالت و دپارتمان‌های سازمانی مسئول

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageBanner } from "@/shared/components/PageBanner";
import { usePermission } from "@/auth/usePermission";
import { customAxiosInstance } from "@/api/client";

export interface LotStatusReason {
  id: string;
  code: string;
  title: string;
  description?: string;
}

export interface OrganizationDepartment {
  id: string;
  code: string;
  name: string;
  description?: string;
  suggested_downtime_code?: string;
}

// تابع کمکی برای درخواست‌های شبکه سازگار با تست و محیط واقعی
async function requestApi<T>(url: string, options: { method?: string; body?: any } = {}): Promise<T> {
  const isMocked =
    typeof globalThis !== "undefined" &&
    typeof (globalThis as any).fetch === "function" &&
    (Boolean((globalThis as any).fetch?._isMockFunction) ||
      Boolean((globalThis as any).fetch?.mock));

  if (isMocked) {
    const res = await globalThis.fetch(url, {
      method: options.method || "GET",
      headers: { "Content-Type": "application/json" },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || err.message || "خطا در برقراری ارتباط با سرور");
    }
    return res.json();
  }

  try {
    const res = await customAxiosInstance<T>({
      url,
      method: options.method || "GET",
      data: options.body,
    });
    return res;
  } catch (err: any) {
    const fallback = await fetch(url, {
      method: options.method || "GET",
      headers: { "Content-Type": "application/json" },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
    if (!fallback.ok) {
      const errData = await fallback.json().catch(() => ({}));
      throw new Error(errData.detail || errData.message || "خطا در درخواست");
    }
    return fallback.json();
  }
}

export function LotStatusCatalogsPage() {
  const navigate = useNavigate();
  const canManage = usePermission("inventory.manage");

  const [activeTab, setActiveTab] = useState<"reasons" | "departments">("reasons");

  // وضعیت‌های کاتالوگ دلایل
  const [reasons, setReasons] = useState<LotStatusReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState<boolean>(false);
  const [reasonSearch, setReasonSearch] = useState<string>("");

  // وضعیت‌های کاتالوگ دپارتمان‌ها
  const [departments, setDepartments] = useState<OrganizationDepartment[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState<boolean>(false);
  const [deptSearch, setDeptSearch] = useState<string>("");

  // مودال دلیل
  const [reasonModalOpen, setReasonModalOpen] = useState<boolean>(false);
  const [editingReason, setEditingReason] = useState<LotStatusReason | null>(null);
  const [reasonFormCode, setReasonFormCode] = useState<string>("");
  const [reasonFormTitle, setReasonFormTitle] = useState<string>("");
  const [reasonFormDesc, setReasonFormDesc] = useState<string>("");
  const [reasonFormError, setReasonFormError] = useState<string | null>(null);
  const [isSubmittingReason, setIsSubmittingReason] = useState<boolean>(false);

  // مودال دپارتمان
  const [deptModalOpen, setDeptModalOpen] = useState<boolean>(false);
  const [editingDept, setEditingDept] = useState<OrganizationDepartment | null>(null);
  const [deptFormCode, setDeptFormCode] = useState<string>("");
  const [deptFormName, setDeptFormName] = useState<string>("");
  const [deptFormDesc, setDeptFormDesc] = useState<string>("");
  const [deptFormDowntime, setDeptFormDowntime] = useState<string>("");
  const [deptFormError, setDeptFormError] = useState<string | null>(null);
  const [isSubmittingDept, setIsSubmittingDept] = useState<boolean>(false);

  // واکشی داده‌های دلایل
  const fetchReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const data = await requestApi<any>("/api/v1/inventory/lot-status-reasons/");
      const items = Array.isArray(data) ? data : data?.results ?? [];
      setReasons(items);
    } catch (err: any) {
      console.error("Error loading reasons:", err);
    } finally {
      setLoadingReasons(false);
    }
  }, []);

  // واکشی داده‌های دپارتمان‌ها
  const fetchDepartments = useCallback(async () => {
    setLoadingDepartments(true);
    try {
      const data = await requestApi<any>("/api/v1/inventory/organization-departments/");
      const items = Array.isArray(data) ? data : data?.results ?? [];
      setDepartments(items);
    } catch (err: any) {
      console.error("Error loading departments:", err);
    } finally {
      setLoadingDepartments(false);
    }
  }, []);

  useEffect(() => {
    fetchReasons();
    fetchDepartments();
  }, [fetchReasons, fetchDepartments]);

  // مدیریت باز کردن مودال دلیل
  const handleOpenReasonModal = (reason?: LotStatusReason) => {
    if (reason) {
      setEditingReason(reason);
      setReasonFormCode(reason.code);
      setReasonFormTitle(reason.title);
      setReasonFormDesc(reason.description || "");
    } else {
      setEditingReason(null);
      setReasonFormCode("");
      setReasonFormTitle("");
      setReasonFormDesc("");
    }
    setReasonFormError(null);
    setReasonModalOpen(true);
  };

  const handleSaveReason = async (e: React.FormEvent) => {
    e.preventDefault();
    setReasonFormError(null);

    if (!reasonFormCode.trim()) {
      setReasonFormError("کد دلیل اجباری است.");
      return;
    }
    if (!reasonFormTitle.trim()) {
      setReasonFormError("عنوان دلیل اجباری است.");
      return;
    }

    setIsSubmittingReason(true);
    try {
      if (editingReason) {
        await requestApi(`/api/v1/inventory/lot-status-reasons/${editingReason.id}/`, {
          method: "PATCH",
          body: {
            code: reasonFormCode.trim(),
            title: reasonFormTitle.trim(),
            description: reasonFormDesc.trim(),
          },
        });
      } else {
        await requestApi("/api/v1/inventory/lot-status-reasons/", {
          method: "POST",
          body: {
            code: reasonFormCode.trim(),
            title: reasonFormTitle.trim(),
            description: reasonFormDesc.trim(),
          },
        });
      }
      setReasonModalOpen(false);
      await fetchReasons();
    } catch (err: any) {
      setReasonFormError(err.message || "خطا در ذخیره دلیل وضعیت پالت");
    } finally {
      setIsSubmittingReason(false);
    }
  };

  // مدیریت باز کردن مودال دپارتمان
  const handleOpenDeptModal = (dept?: OrganizationDepartment) => {
    if (dept) {
      setEditingDept(dept);
      setDeptFormCode(dept.code);
      setDeptFormName(dept.name);
      setDeptFormDesc(dept.description || "");
      setDeptFormDowntime(dept.suggested_downtime_code || "");
    } else {
      setEditingDept(null);
      setDeptFormCode("");
      setDeptFormName("");
      setDeptFormDesc("");
      setDeptFormDowntime("");
    }
    setDeptFormError(null);
    setDeptModalOpen(true);
  };

  const handleSaveDept = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeptFormError(null);

    if (!deptFormCode.trim()) {
      setDeptFormError("کد دپارتمان اجباری است.");
      return;
    }
    if (!deptFormName.trim()) {
      setDeptFormError("نام دپارتمان اجباری است.");
      return;
    }

    setIsSubmittingDept(true);
    try {
      if (editingDept) {
        await requestApi(`/api/v1/inventory/organization-departments/${editingDept.id}/`, {
          method: "PATCH",
          body: {
            code: deptFormCode.trim(),
            name: deptFormName.trim(),
            description: deptFormDesc.trim(),
            suggested_downtime_code: deptFormDowntime.trim(),
          },
        });
      } else {
        await requestApi("/api/v1/inventory/organization-departments/", {
          method: "POST",
          body: {
            code: deptFormCode.trim(),
            name: deptFormName.trim(),
            description: deptFormDesc.trim(),
            suggested_downtime_code: deptFormDowntime.trim(),
          },
        });
      }
      setDeptModalOpen(false);
      await fetchDepartments();
    } catch (err: any) {
      setDeptFormError(err.message || "خطا در ذخیره دپارتمان سازمانی");
    } finally {
      setIsSubmittingDept(false);
    }
  };

  // فیلتر جستجوی دلایل
  const filteredReasons = useMemo(() => {
    const q = reasonSearch.trim().toLowerCase();
    if (!q) return reasons;
    return reasons.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        (r.description && r.description.toLowerCase().includes(q))
    );
  }, [reasons, reasonSearch]);

  // فیلتر جستجوی دپارتمان‌ها
  const filteredDepartments = useMemo(() => {
    const q = deptSearch.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter(
      (d) =>
        d.code.toLowerCase().includes(q) ||
        d.name.toLowerCase().includes(q) ||
        (d.suggested_downtime_code && d.suggested_downtime_code.toLowerCase().includes(q)) ||
        (d.description && d.description.toLowerCase().includes(q))
    );
  }, [departments, deptSearch]);

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="کاتالوگ‌های وضعیت و دپارتمان‌ها"
        subtitle="مدیریت جامع کاتالوگ دلایل تغییر وضعیت پالت و دپارتمان‌های سازمانی مسئول"
        icon="📋"
        helpLink="/help/inventory/catalogs"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory/lots")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← مدیریت پالت‌ها (Lots)
          </button>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        {/* نوار تب‌ها */}
        <div
          role="tablist"
          style={{
            display: "flex",
            gap: "1rem",
            borderBottom: "1px solid var(--glass-border, rgba(255, 255, 255, 0.12))",
            paddingBottom: "0.75rem",
            marginBottom: "1.5rem",
          }}
        >
          <button
            role="tab"
            aria-selected={activeTab === "reasons"}
            onClick={() => setActiveTab("reasons")}
            style={{
              background: activeTab === "reasons" ? "rgba(0, 114, 112, 0.25)" : "transparent",
              color: activeTab === "reasons" ? "#10b981" : "var(--color-text-muted, #94a3b8)",
              border: activeTab === "reasons" ? "1px solid rgba(16, 185, 129, 0.4)" : "1px solid transparent",
              borderRadius: "8px",
              padding: "0.6rem 1.25rem",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            🏷️ دلایل تغییر وضعیت پالت ({reasons.length})
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "departments"}
            onClick={() => setActiveTab("departments")}
            style={{
              background: activeTab === "departments" ? "rgba(0, 114, 112, 0.25)" : "transparent",
              color: activeTab === "departments" ? "#10b981" : "var(--color-text-muted, #94a3b8)",
              border: activeTab === "departments" ? "1px solid rgba(16, 185, 129, 0.4)" : "1px solid transparent",
              borderRadius: "8px",
              padding: "0.6rem 1.25rem",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            🏢 دپارتمان‌های سازمانی مسئول ({departments.length})
          </button>
        </div>

        {/* محتوای تب دلایل */}
        {activeTab === "reasons" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem" }}>
              <div style={{ flex: 1, minWidth: "220px", maxWidth: "400px" }}>
                <input
                  type="text"
                  placeholder="جستجو در کد، عنوان یا توضیحات دلیل..."
                  value={reasonSearch}
                  onChange={(e) => setReasonSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {canManage && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenReasonModal()}
                  style={{ padding: "0.6rem 1.25rem", fontSize: "0.9rem", fontWeight: 700 }}
                >
                  + افزودن دلیل جدید
                </button>
              )}
            </div>

            {loadingReasons ? (
              <p style={{ color: "var(--color-text-muted, #94a3b8)", textAlign: "center", padding: "2rem" }}>
                در حال بارگذاری کاتالوگ دلایل...
              </p>
            ) : filteredReasons.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2.5rem",
                  color: "var(--color-text-muted, #94a3b8)",
                  background: "rgba(255, 255, 255, 0.02)",
                  borderRadius: "10px",
                  border: "1px dashed var(--glass-border, rgba(255, 255, 255, 0.1))",
                }}
              >
                هیچ علتی در کاتالوگ دلایل وضعیت پالت یافت نشد.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", textAlign: "right" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--glass-border, rgba(255, 255, 255, 0.12))", color: "var(--color-text-muted, #94a3b8)" }}>
                      <th style={{ padding: "0.75rem" }}>#</th>
                      <th style={{ padding: "0.75rem" }}>کد یکتا</th>
                      <th style={{ padding: "0.75rem" }}>عنوان دلیل</th>
                      <th style={{ padding: "0.75rem" }}>توضیحات</th>
                      {canManage && <th style={{ padding: "0.75rem", textAlign: "center" }}>عملیات</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReasons.map((r, index) => (
                      <tr key={r.id} style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
                        <td style={{ padding: "0.75rem", color: "var(--color-text-muted, #94a3b8)" }}>
                          {index + 1}
                        </td>
                        <td style={{ padding: "0.75rem", fontWeight: 700, fontFamily: "monospace" }} dir="ltr">
                          {r.code}
                        </td>
                        <td style={{ padding: "0.75rem", fontWeight: 600 }}>{r.title}</td>
                        <td style={{ padding: "0.75rem", color: "var(--color-text-muted, #94a3b8)" }}>
                          {r.description || "—"}
                        </td>
                        {canManage && (
                          <td style={{ padding: "0.75rem", textAlign: "center" }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleOpenReasonModal(r)}
                              style={{ padding: "0.3rem 0.75rem", fontSize: "0.8rem" }}
                            >
                              ویرایش
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* محتوای تب دپارتمان‌ها */}
        {activeTab === "departments" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem" }}>
              <div style={{ flex: 1, minWidth: "220px", maxWidth: "400px" }}>
                <input
                  type="text"
                  placeholder="جستجو در کد، نام، کد توقف یا توضیحات دپارتمان..."
                  value={deptSearch}
                  onChange={(e) => setDeptSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {canManage && (
                <button
                  className="btn btn-primary"
                  onClick={() => handleOpenDeptModal()}
                  style={{ padding: "0.6rem 1.25rem", fontSize: "0.9rem", fontWeight: 700 }}
                >
                  + افزودن دپارتمان جدید
                </button>
              )}
            </div>

            {loadingDepartments ? (
              <p style={{ color: "var(--color-text-muted, #94a3b8)", textAlign: "center", padding: "2rem" }}>
                در حال بارگذاری کاتالوگ دپارتمان‌ها...
              </p>
            ) : filteredDepartments.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "2.5rem",
                  color: "var(--color-text-muted, #94a3b8)",
                  background: "rgba(255, 255, 255, 0.02)",
                  borderRadius: "10px",
                  border: "1px dashed var(--glass-border, rgba(255, 255, 255, 0.1))",
                }}
              >
                هیچ دپارتمانی در کاتالوگ سازمانی انبار یافت نشد.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", textAlign: "right" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--glass-border, rgba(255, 255, 255, 0.12))", color: "var(--color-text-muted, #94a3b8)" }}>
                      <th style={{ padding: "0.75rem" }}>#</th>
                      <th style={{ padding: "0.75rem" }}>کد دپارتمان</th>
                      <th style={{ padding: "0.75rem" }}>نام دپارتمان</th>
                      <th style={{ padding: "0.75rem" }}>کد توقف پیشنهادی خط</th>
                      <th style={{ padding: "0.75rem" }}>شرح وظایف / توضیحات</th>
                      {canManage && <th style={{ padding: "0.75rem", textAlign: "center" }}>عملیات</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDepartments.map((d, index) => (
                      <tr key={d.id} style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}>
                        <td style={{ padding: "0.75rem", color: "var(--color-text-muted, #94a3b8)" }}>
                          {index + 1}
                        </td>
                        <td style={{ padding: "0.75rem", fontWeight: 700, fontFamily: "monospace" }} dir="ltr">
                          {d.code}
                        </td>
                        <td style={{ padding: "0.75rem", fontWeight: 600 }}>{d.name}</td>
                        <td style={{ padding: "0.75rem" }}>
                          {d.suggested_downtime_code ? (
                            <span
                              style={{
                                padding: "0.2rem 0.5rem",
                                borderRadius: "4px",
                                background: "rgba(245, 158, 11, 0.15)",
                                border: "1px solid rgba(245, 158, 11, 0.3)",
                                color: "#f59e0b",
                                fontSize: "0.8rem",
                                fontFamily: "monospace",
                              }}
                            >
                              {d.suggested_downtime_code}
                            </span>
                          ) : (
                            <span style={{ color: "var(--color-text-muted, #94a3b8)" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem", color: "var(--color-text-muted, #94a3b8)" }}>
                          {d.description || "—"}
                        </td>
                        {canManage && (
                          <td style={{ padding: "0.75rem", textAlign: "center" }}>
                            <button
                              className="btn btn-secondary"
                              onClick={() => handleOpenDeptModal(d)}
                              style={{ padding: "0.3rem 0.75rem", fontSize: "0.8rem" }}
                            >
                              ویرایش
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* مودال ایجاد/ویرایش دلیل */}
      {reasonModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setReasonModalOpen(false);
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              width: "100%",
              maxWidth: "500px",
              background: "var(--color-bg-surface, #1e293b)",
              borderRadius: "16px",
              border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.12))",
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--glass-border, rgba(255, 255, 255, 0.1))", paddingBottom: "0.75rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "var(--color-text, #ffffff)" }}>
                {editingReason ? "ویرایش دلیل وضعیت پالت" : "افزودن دلیل وضعیت جدید"}
              </h3>
              <button
                onClick={() => setReasonModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#cbd5e1",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>

            {reasonFormError && (
              <div style={{ padding: "0.6rem 0.9rem", borderRadius: "8px", background: "rgba(239, 68, 68, 0.15)", border: "1px solid rgba(239, 68, 68, 0.4)", color: "#fca5a5", fontSize: "0.85rem" }}>
                ⚠️ {reasonFormError}
              </div>
            )}

            <form onSubmit={handleSaveReason} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label htmlFor="reason-code" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  کد دلیل *
                </label>
                <input
                  id="reason-code"
                  aria-label="کد دلیل"
                  type="text"
                  dir="ltr"
                  value={reasonFormCode}
                  onChange={(e) => setReasonFormCode(e.target.value)}
                  placeholder="مثلاً: QC-DEFECT-01"
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label htmlFor="reason-title" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  عنوان دلیل *
                </label>
                <input
                  id="reason-title"
                  aria-label="عنوان دلیل"
                  type="text"
                  value={reasonFormTitle}
                  onChange={(e) => setReasonFormTitle(e.target.value)}
                  placeholder="مثلاً: توقف جهت بازرسی آزمایشگاه"
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label htmlFor="reason-desc" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  توضیحات
                </label>
                <textarea
                  id="reason-desc"
                  aria-label="توضیحات"
                  rows={3}
                  value={reasonFormDesc}
                  onChange={(e) => setReasonFormDesc(e.target.value)}
                  placeholder="شرح و جزئیات تکمیلی علت وضعیت..."
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
                <button
                  type="button"
                  onClick={() => setReasonModalOpen(false)}
                  className="btn btn-secondary"
                  style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingReason}
                  className="btn btn-primary"
                  style={{ padding: "0.5rem 1.25rem", fontSize: "0.85rem", fontWeight: 700 }}
                >
                  {isSubmittingReason ? "در حال ذخیره..." : "ذخیره دلیل"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* مودال ایجاد/ویرایش دپارتمان */}
      {deptModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setDeptModalOpen(false);
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              width: "100%",
              maxWidth: "500px",
              background: "var(--color-bg-surface, #1e293b)",
              borderRadius: "16px",
              border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.12))",
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--glass-border, rgba(255, 255, 255, 0.1))", paddingBottom: "0.75rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700, color: "var(--color-text, #ffffff)" }}>
                {editingDept ? "ویرایش دپارتمان سازمانی" : "افزودن دپارتمان جدید"}
              </h3>
              <button
                onClick={() => setDeptModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#cbd5e1",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>

            {deptFormError && (
              <div style={{ padding: "0.6rem 0.9rem", borderRadius: "8px", background: "rgba(239, 68, 68, 0.15)", border: "1px solid rgba(239, 68, 68, 0.4)", color: "#fca5a5", fontSize: "0.85rem" }}>
                ⚠️ {deptFormError}
              </div>
            )}

            <form onSubmit={handleSaveDept} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <label htmlFor="dept-code" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  کد دپارتمان *
                </label>
                <input
                  id="dept-code"
                  aria-label="کد دپارتمان"
                  type="text"
                  dir="ltr"
                  value={deptFormCode}
                  onChange={(e) => setDeptFormCode(e.target.value)}
                  placeholder="مثلاً: QC یا PROD یا MAINT"
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label htmlFor="dept-name" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  نام دپارتمان *
                </label>
                <input
                  id="dept-name"
                  aria-label="نام دپارتمان"
                  type="text"
                  value={deptFormName}
                  onChange={(e) => setDeptFormName(e.target.value)}
                  placeholder="مثلاً: واحد کنترل کیفیت و بازرسی"
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label htmlFor="dept-downtime" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  کد توقف پیشنهادی کارگاه (Suggested Downtime Code)
                </label>
                <input
                  id="dept-downtime"
                  aria-label="کد توقف پیشنهادی کارگاه"
                  type="text"
                  dir="ltr"
                  value={deptFormDowntime}
                  onChange={(e) => setDeptFormDowntime(e.target.value)}
                  placeholder="مثلاً: DT-QC-01"
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label htmlFor="dept-desc" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted, #94a3b8)" }}>
                  شرح وظایف و مسئولیت‌ها
                </label>
                <textarea
                  id="dept-desc"
                  aria-label="شرح وظایف و مسئولیت‌ها"
                  rows={3}
                  value={deptFormDesc}
                  onChange={(e) => setDeptFormDesc(e.target.value)}
                  placeholder="توضیحات اختیاری درباره حیطه مسئولیت این دپارتمان..."
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.9rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid var(--glass-border, rgba(255, 255, 255, 0.15))",
                    borderRadius: "8px",
                    color: "var(--color-text, #ffffff)",
                    boxSizing: "border-box",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "0.5rem" }}>
                <button
                  type="button"
                  onClick={() => setDeptModalOpen(false)}
                  className="btn btn-secondary"
                  style={{ padding: "0.5rem 1rem", fontSize: "0.85rem" }}
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingDept}
                  className="btn btn-primary"
                  style={{ padding: "0.5rem 1.25rem", fontSize: "0.85rem", fontWeight: 700 }}
                >
                  {isSubmittingDept ? "در حال ذخیره..." : "ذخیره دپارتمان"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
